import React from 'react';
import clsx from 'clsx';
import { DragDrop, StatusBar, useUppyEvent } from '@uppy/react';
import '@uppy/core/dist/style.min.css';
import '@uppy/drag-drop/dist/style.min.css';
import '@uppy/status-bar/dist/style.min.css';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { assignDataToDictionaryUpload, useUppyTus, uppyTusBaseOptions } from '@/utils/uppy';
import findResult from '@/utils/resultsHelper';
import { uploadEndpointDictionaryData } from '@/constants/urls';
import {
  datasetDictionaryFileRemoved,
  datasetDictionaryMessages,
  datasetDictionaryUpload,
  datasetDictionaryUploadFail,
  datasetDictionaryUploadSuccess,
} from '../actions';

const useUppyEventHandlers = (uppy, intl, {
  startUpload,
  uploadSuccess,
  uploadFail,
  dictionaryUploadMessages,
  dictionaryRemovedByUser,
  category,
  code,
}) => {
  useUppyEvent(uppy, 'upload', () => {
    const { info } = uppy.getState();
    if (String(info.message).includes('only upload 1')) {
      dictionaryUploadMessages(category, code, intl.formatMessage({ id: 'dataset.convert.only.one.file' }));
      uppy.cancelAll();
      setTimeout(() => {
        dictionaryUploadMessages(category, code, '');
      }, 6000);
    } else {
      startUpload(category, code);
    }
  });

  useUppyEvent(uppy, 'complete', (result) => {
    if (result?.failed?.length) {
      uploadFail(category, code);
    }
  });

  useUppyEvent(uppy, 'restriction-failed', (_, error) => {
    const errorMessage = String(error.message);

    if (errorMessage.includes('only upload 1')) {
      dictionaryUploadMessages(category, code, intl.formatMessage({ id: 'dataset.convert.only.one.file' }));
      setTimeout(() => {
        dictionaryUploadMessages(category, code, '');
      }, 6000);
    }

    if (errorMessage.includes('only upload:')) {
      dictionaryUploadMessages(category, code, intl.formatMessage({ id: 'dataset.convert.only.csv' }));
      setTimeout(() => {
        dictionaryUploadMessages(category, code, '');
      }, 6000);
    }
    if (errorMessage.includes('Cannot add more files')) {
      dictionaryUploadMessages(category, code, intl.formatMessage({ id: 'dataset.convert.already.uploading' }));
      setTimeout(() => {
        dictionaryUploadMessages(category, code, '');
      }, 6000);
    }
  });

  useUppyEvent(uppy, 'upload-success', () => {
    uploadSuccess(category, code);

    setTimeout(() => {
      uppy.cancelAll();
    }, 3000);
  });

  useUppyEvent(uppy, 'upload-error', (file, error) => {
    if (error) {
      dictionaryUploadMessages(category, code, intl.formatMessage({ id: 'dataset.convert.upload.failed' }), error);
    }
    setTimeout(() => {
      dictionaryUploadMessages(category, code, '');
    }, 6000);
  });

  useUppyEvent(uppy, 'file-removed', () => {
    dictionaryRemovedByUser(category, code);
  });
};

const DictionaryDataUpload = ({
  dictionaryCode,
  dictionaryFieldTable,
  startUpload,
  uploadSuccess,
  uploadFail,
  dictionaryUploadMessages,
  dictionaryRemovedByUser,
  category,
  code,
}) => {
  const intl = useIntl();
  const tusOptions = React.useMemo(() => ({
    ...uppyTusBaseOptions,
    id: `dictionary-upload-${dictionaryCode}`,
    endpoint: uploadEndpointDictionaryData(dictionaryCode),
    headers: { 'ARIDHIA-FAIR-Parent-Model-ID': dictionaryCode },
  }), [dictionaryCode]);

  const uppy = useUppyTus(assignDataToDictionaryUpload, tusOptions);

  useUppyEventHandlers(uppy, intl, {
    startUpload,
    uploadSuccess,
    uploadFail,
    dictionaryUploadMessages,
    dictionaryRemovedByUser,
    category,
    code,
  });

  return (
    <>
      <DragDrop
        uppy={uppy}
        locale={{
          strings: {
            browse: intl.formatMessage({ id: 'upload.dashboard.browse' }),
            dropHereOr: `${intl.formatMessage({ id: 'upload.dictionary.drop.files' })} %{browse}`,
          },
        }}
      />
      <div className={clsx(dictionaryFieldTable
        ? 'absolute right-10 top-0 flex items-center h-100p aridhia-dictionary-table-uppy-statusbar'
        : 'aridhia-dictionary-uppy-statusbar')}
      >
        <StatusBar
          uppy={uppy}
          hideAfterFinish={false}
          replaceTargetContent={false}
          locale={{
            strings: {
              retry: '',
              complete: intl.formatMessage({ id: 'dataset.convert.uploaded' }),
            },
          }}
        />
      </div>
    </>
  );
};

const mapStateToProps = ({
  datasets: { datasets },
}, { category, code }) => {
  const dataset = findResult(datasets, category, code);
  const dictionaries = dataset ? dataset.dictionaries : [];
  const dictionary = dictionaries.find(({ name }) => name === dataset.activeDictionaryName);

  return (
    {
      dictionaries,
      dictionaryCode: dictionary ? dictionary.code : null,
    }
  );
};

const mapDispatchToProps = {
  startUpload: datasetDictionaryUpload,
  uploadSuccess: datasetDictionaryUploadSuccess,
  uploadFail: datasetDictionaryUploadFail,
  dictionaryUploadMessages: datasetDictionaryMessages,
  dictionaryRemovedByUser: datasetDictionaryFileRemoved,
};

export default connect(mapStateToProps, mapDispatchToProps)(DictionaryDataUpload);
